import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatCurrency, publicPayUrl, publicPdfUrl } from "@/lib/api";
import { Download, Link as LinkIcon, Check, Trash2, ArrowLeft } from "lucide-react";

const StatusBadge = ({ status }) => {
  const isPaid = status === "paid";
  return (
    <span
      data-testid={`detail-status-${status}`}
      className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
        isPaid
          ? "bg-emerald-50 text-emerald-800 border-emerald-200"
          : "bg-amber-50 text-amber-800 border-amber-200"
      }`}
    >
      {status}
    </span>
  );
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api
      .get(`/invoices/${id}`)
      .then((r) => setInvoice(r.data))
      .catch(() => toast.error("Invoice not found"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const markPaid = async () => {
    try {
      const { data } = await api.post(`/invoices/${id}/mark-paid`);
      setInvoice(data);
      toast.success("Marked as paid");
    } catch {
      toast.error("Failed to mark as paid");
    }
  };

  const copyLink = async () => {
    const url = publicPayUrl(id);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Payment link copied", { description: url });
    } catch {
      window.prompt("Copy link", url);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this invoice? This cannot be undone.")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      navigate("/");
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (loading)
    return <p className="text-zinc-400 text-sm">Loading invoice...</p>;
  if (!invoice)
    return (
      <div className="text-center py-20">
        <p className="text-zinc-500">Invoice not found.</p>
        <Link to="/" className="underline text-zinc-900 mt-2 inline-block">
          Back to dashboard
        </Link>
      </div>
    );

  return (
    <div className="space-y-10" data-testid="invoice-detail-view">
      <div>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
              Invoice {invoice.invoice_number}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-zinc-900 mt-1">
              {invoice.client_name}
            </h1>
            <p className="text-zinc-500 mt-1">
              Issued {invoice.created_at?.slice(0, 10)}
              {invoice.paid_at && ` · Paid ${invoice.paid_at.slice(0, 10)}`}
            </p>
          </div>
          <StatusBadge status={invoice.status} />
        </div>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-3 border-y border-zinc-200 py-4">
        <button
          onClick={copyLink}
          data-testid="copy-link-button"
          className="inline-flex items-center gap-2 bg-zinc-900 text-white rounded-none px-5 py-2.5 text-sm font-medium hover:bg-zinc-800 transition-colors"
        >
          <LinkIcon size={14} /> Copy payment link
        </button>
        <a
          href={publicPdfUrl(invoice.id)}
          target="_blank"
          rel="noreferrer"
          data-testid="download-pdf-button"
          className="inline-flex items-center gap-2 bg-transparent text-zinc-900 border border-zinc-200 rounded-none px-5 py-2.5 text-sm font-medium hover:border-zinc-900 transition-colors"
        >
          <Download size={14} /> Download PDF
        </a>
        {invoice.status !== "paid" && (
          <button
            onClick={markPaid}
            data-testid="mark-paid-button"
            className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded-none px-5 py-2.5 text-sm font-medium hover:bg-emerald-700 transition-colors"
          >
            <Check size={14} /> Mark as paid
          </button>
        )}
        <button
          onClick={remove}
          data-testid="delete-invoice-button"
          className="inline-flex items-center gap-2 bg-transparent text-zinc-500 border border-zinc-200 rounded-none px-5 py-2.5 text-sm font-medium hover:border-red-500 hover:text-red-600 transition-colors ml-auto"
        >
          <Trash2 size={14} /> Delete
        </button>
      </div>

      {/* Meta */}
      <div className="grid md:grid-cols-3 gap-x-12 gap-y-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            Billed To
          </p>
          <p className="text-zinc-900 mt-2">{invoice.client_name}</p>
          {invoice.client_email && (
            <p className="text-zinc-500 text-sm">{invoice.client_email}</p>
          )}
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            From
          </p>
          <p className="text-zinc-900 mt-2">InvoiceApp</p>
          <p className="text-zinc-500 text-sm">admin@invoiceapp.com</p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            Public link
          </p>
          <p
            className="text-zinc-900 mt-2 font-mono text-xs break-all"
            data-testid="public-link-display"
          >
            {publicPayUrl(invoice.id)}
          </p>
        </div>
      </div>

      {/* Items */}
      <div className="border-t border-zinc-900">
        <div className="grid grid-cols-12 gap-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200">
          <div className="col-span-6">Description</div>
          <div className="col-span-2 text-right">Qty</div>
          <div className="col-span-2 text-right">Rate</div>
          <div className="col-span-2 text-right">Amount</div>
        </div>
        {invoice.line_items.map((it, idx) => (
          <div
            key={idx}
            className="grid grid-cols-12 gap-4 py-5 border-b border-zinc-200 fade-up"
            style={{ animationDelay: `${idx * 60}ms` }}
            data-testid={`detail-item-${idx}`}
          >
            <div className="col-span-6 text-zinc-900">{it.description}</div>
            <div className="col-span-2 text-right text-zinc-500">
              {Number(it.quantity)}
            </div>
            <div className="col-span-2 text-right text-zinc-500">
              {formatCurrency(it.rate, invoice.currency)}
            </div>
            <div className="col-span-2 text-right text-zinc-900 font-medium">
              {formatCurrency(it.quantity * it.rate, invoice.currency)}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <div className="w-full md:w-80 space-y-3">
          <div className="flex justify-between text-zinc-500 text-sm">
            <span className="font-bold uppercase tracking-[0.2em] text-[10px]">
              Subtotal
            </span>
            <span>{formatCurrency(invoice.subtotal, invoice.currency)}</span>
          </div>
          <div className="flex justify-between border-t border-zinc-900 pt-3 items-baseline">
            <span className="font-display text-lg font-medium">Total</span>
            <span
              className="font-display text-3xl font-medium"
              data-testid="detail-total"
            >
              {formatCurrency(invoice.total, invoice.currency)}
            </span>
          </div>
        </div>
      </div>

      {invoice.notes && (
        <div className="border-t border-zinc-200 pt-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            Notes
          </p>
          <p className="text-zinc-700 mt-2 whitespace-pre-line">{invoice.notes}</p>
        </div>
      )}
    </div>
  );
}
